import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import useBrand from './useBrand';
import useUser from './useUser';
import {
    CurrentBrand,
    OrganizationInvitationStatus,
    OrganizationStatus,
    OrganizationTeamData
} from 'modules/Types';
import { OrganizationData } from 'modules/Types';
import {
    inviteOrganizationUser_api,
    createOrganization_api,
    getOrganization_api,
    deleteOrganizationBrand_api,
    deleteOrganizationUser_api,
    updateOrganizationUser_api,
    updateUserBrand_api,
    createOrganizationBrand_api,
    respondOrganizationUserInvite_api,
    checkOrganizationUserInvitationStatus_api,
    updateOrganization_api,
    deleteOrganization_api
} from 'api/organizationV2Api';
import { OrganizationRole, PlanType, UserPermissionOnBrand } from 'modules/Const';
import { deepCopy } from 'modules/Utils';

interface OrganizationState {
    organization: OrganizationData | undefined;
    organizationUsers: any[] | undefined;
    organizationBrandIds: number[] | undefined;

    isLoadingOrganization: boolean;
    isOrganizationInitialized: boolean;

    isLoadingOrganizationAction: boolean;
    isLoadingAddBrandToOrganization: boolean;

    invitationStatus: OrganizationInvitationStatus | undefined;

    hasOrganizationAdminAccess: () => boolean;

    setOrganization: (organization: OrganizationData | undefined) => void;
    setIsLoadingOrganizationAction: (isLoading: boolean) => void;
    setIsLoadingAddBrandToOrganization: (isLoading: boolean) => void;
    setInvitationStatus: (invitationStatus: OrganizationInvitationStatus) => void;
    setIsOrganizationInitialized: (isInitialized: boolean) => void;

    resetOrganization: () => void;

    getOrganization: ({
        organizationId,
        brandId,
        userId,
    }: {
        organizationId?: number;
        brandId?: number;
        userId?: number;
    }) => Promise<OrganizationTeamData | null | undefined>;

    createOrganization: ({
        name,
        emails,
        setupStatus,
        ownerId,
    }: {
        name: string;
        emails: string[];
        setupStatus: OrganizationStatus;
        ownerId: number;
    }) => Promise<OrganizationTeamData | null | undefined>;

    updateOrganization: ({
        organizationId,
        name,
        setupStatus,
    }: {
        organizationId: number;
        name: string;
        setupStatus: OrganizationStatus;
    }) => Promise<void>;

    inviteOrganizationUsers: ({
        organizationId,
        users,
        brands,
    }: {
        organizationId: number;
        users: {
            email: string;
            role: OrganizationRole;
            firstName: string;
            lastName: string;
        }[];
        brands: CurrentBrand[];
    }) => Promise<OrganizationTeamData | null | undefined>;

    respondOrganizationUserInvite: ({
        organizationId,
        userId,
        invitationStatus,
        brandIds,
    }: {
        organizationId: number;
        userId: number;
        invitationStatus: OrganizationInvitationStatus;
        brandIds?: number[];
    }) => Promise<void>;

    checkOrganizationUserInvitationStatus: ({
        organizationId,
        userId,
    }: {
        organizationId: number;
        userId: number;
    }) => Promise<OrganizationInvitationStatus | null | undefined>;

    createOrganizationBrand: ({
        organizationId,
        brandId,
    }: {
        organizationId: number;
        brandId: number;
    }) => Promise<void>;

    updateOrganizationUser: ({
        organizationId,
        userId,
        role,
        brands,
    }: {
        organizationId: number;
        userId: number;
        role: OrganizationRole;
        brands: any[];
    }) => Promise<void>;

    updateUserBrand: ({
        organizationId,
        brandId,
        users,
    }: {
        organizationId: number;
        brandId: number;
        users: {
            userId: number;
            permissionLevel: UserPermissionOnBrand;
        }[];
    }) => Promise<void>;

    deleteOrganizationBrand: ({
        organizationId,
        brandId,
    }: {
        organizationId: number;
        brandId: number;
    }) => Promise<void>;

    deleteOrganizationUser: ({
        organizationId,
        userId,
    }: {
        organizationId: number;
        userId: number;
    }) => Promise<void>;

    deleteOrganization: () => Promise<void>;
}

const useOrganizationV2 = create(
    immer<OrganizationState>((set, get) => ({
        // State
        organization: undefined,
        organizationUsers: [],
        organizationBrandIds: [],
        isLoadingOrganization: false,
        isOrganizationInitialized: false,

        isLoadingOrganizationAction: false,
        isLoadingAddBrandToOrganization: false,
        invitationStatus: undefined,

        setOrganization: (organization: OrganizationData | undefined) => set(state => { state.organization = organization }),
        setIsLoadingOrganizationAction: (isLoading: boolean) => set(state => { state.isLoadingOrganizationAction = isLoading }),
        setIsLoadingAddBrandToOrganization: (isLoading: boolean) => set(state => { state.isLoadingAddBrandToOrganization = isLoading }),
        setInvitationStatus: (invitationStatus: OrganizationInvitationStatus) => set(state => { state.invitationStatus = invitationStatus }),
        setIsOrganizationInitialized: (isInitialized: boolean) => set(state => { state.isOrganizationInitialized = isInitialized }),

        resetOrganization: () => set(state => {
            state.organization = undefined;
            state.organizationUsers = [];
            state.organizationBrandIds = [];
            state.invitationStatus = undefined;
        }),

        hasOrganizationAdminAccess: (): boolean => {
            const { organization, organizationUsers } = get();
            const { currentUser, isSymphonyAdmin } = useUser.getState();
            if (!organization) {
                return false;
            }
            if (isSymphonyAdmin) {
                return true;
            }
            if (organization.owner_id === currentUser?.id) {
                return true;
            }
            const role = organizationUsers?.find(ou => ou.user_id === currentUser?.id)?.role || OrganizationRole.MEMBER;
            return role === OrganizationRole.ADMINISTRATOR || role === OrganizationRole.OWNER
        },

        getOrganization: async ({
            organizationId,
            brandId,
            userId,
        }: {
            organizationId?: number;
            brandId?: number;
            userId?: number;
        }): Promise<OrganizationTeamData | null | undefined> => {
            const { resetOrganization } = get();
            const { isSymphonyAdmin } = useUser.getState();
            const { updateOrAddBrands, loadedBrandsDict } = useBrand.getState();

            set({ isLoadingOrganization: true });
            try {
                const res = await getOrganization_api({
                    organizationId,
                    brandId,
                    userId,
                });

                if (res) {
                    const invitationStatus = res.organizationUsers.find(ou => ou.user_id === userId)?.invitation_status;
                    const brands = res.organizationBrands.map(ob => ob.brand);
                    const filteredBrands = brands.filter(newBrand => !loadedBrandsDict?.[newBrand.id])

                    // Only update with brands that aren't already loaded
                    if (filteredBrands.length > 0 && invitationStatus === OrganizationInvitationStatus.ACCEPTED) {
                        updateOrAddBrands(filteredBrands);
                    }

                    set({
                        invitationStatus,
                        organization: res.organization,
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: res.organizationBrands.map(ob => ob.brand_id),
                    });
                } else {
                    resetOrganization();
                }

                return res;
            } catch (error) {
                console.error('Failed to fetch organization data:', error);
                return null;
            } finally {
                set({
                    isOrganizationInitialized: true,
                    isLoadingOrganization: false,
                });
            }
        },

        createOrganization: async ({
            name,
            emails,
            setupStatus,
            ownerId,
        }: {
            name: string;
            emails: string[];
            setupStatus: OrganizationStatus;
            ownerId: number;
        }): Promise<OrganizationTeamData | null | undefined> => {
            set(state => { state.isLoadingOrganization = true });
            try {
                const res = await createOrganization_api({
                    name,
                    emails,
                    setupStatus,
                    ownerId,
                });
                console.log('createOrganization', res);

                if (res) {
                    set({
                        organization: res.organization,
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: res.organizationBrands.map(ob => ob.brand_id),
                    });
                }

                return res;
            } catch (error) {
                console.error('Failed to create organization:', error);
                return null;
            } finally {
                set({ isLoadingOrganization: false });
            }
        },

        updateOrganization: async ({
            organizationId,
            name,
            setupStatus,
        }: {
            organizationId: number;
            name: string;
            setupStatus: OrganizationStatus;
        }): Promise<void> => {
            set({ isLoadingOrganization: true });
            try {
                const res = await updateOrganization_api({
                    organizationId,
                    name,
                    setupStatus,
                });

                if (res) {
                    set({
                        organization: res.organization,
                    });
                }
            } catch (error) {
                console.error('Failed to update organization:', error);
            } finally {
                set({ isLoadingOrganization: false });
            }
        },

        inviteOrganizationUsers: async ({
            organizationId,
            users,
            brands,
        }: {
            organizationId: number;
            users: {
                email: string;
                role: OrganizationRole;
                firstName: string;
                lastName: string;
            }[];
            brands: CurrentBrand[];
        }): Promise<OrganizationTeamData | null | undefined> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await inviteOrganizationUser_api({
                    organizationId,
                    users,
                    brands,
                });

                if (res) {
                    set({
                        organizationUsers: res.organizationUsers,
                    });
                }

                return res;
            } catch (error) {
                console.error('Failed to add organization user:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        respondOrganizationUserInvite: async ({
            organizationId,
            userId,
            invitationStatus,
            brandIds,
        }: {
            organizationId: number;
            userId: number;
            invitationStatus: OrganizationInvitationStatus;
            brandIds?: number[];
        }): Promise<void> => {
            const { updateOrAddBrands, getBrands } = useBrand.getState();
            set({ isLoadingOrganizationAction: true });

            try {
                const res = await respondOrganizationUserInvite_api({
                    organizationId,
                    userId,
                    invitationStatus,
                    brandIds,
                });

                if (res) {
                    const brandsToAdd = res.organizationBrands.map(ob => ob.brand);
                    updateOrAddBrands(brandsToAdd);
                    set({
                        invitationStatus,
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: res.organizationBrands.map(ob => ob.brand_id),
                    });

                    await getBrands();
                }
            } catch (error) {
                console.error('Failed to respond to organization user invite:', error);
            } finally {
                set(state => { state.isLoadingOrganizationAction = false });
            }
        },

        checkOrganizationUserInvitationStatus: async ({
            organizationId,
            userId,
        }: {
            organizationId: number;
            userId: number;
        }): Promise<OrganizationInvitationStatus | null | undefined> => {
            set({ isLoadingOrganizationAction: true });

            try {
                const res = await checkOrganizationUserInvitationStatus_api({
                    organizationId,
                    userId,
                });

                return res?.invitationStatus;
            } catch (error) {
                console.error('Failed to check organization user invitation status:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        createOrganizationBrand: async ({
            organizationId,
            brandId,
        }: {
            organizationId: number;
            brandId: number;
        }): Promise<void> => {
            const { updateOrAddBrand, loadedBrandsDict } = useBrand.getState();
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await createOrganizationBrand_api({
                    organizationId,
                    brandId,
                });

                if (res) {
                    set({
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: res.organizationBrands.map(ob => ob.brand_id),
                    });
                }
                const brand = deepCopy(loadedBrandsDict?.[brandId]);
                if (brand) {
                    brand.tier = PlanType.TEAM;
                    updateOrAddBrand(brand);
                }
            } catch (error) {
                console.error('Failed to create organization brand:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        updateOrganizationUser: async ({
            organizationId,
            userId,
            role,
            brands,
        }: {
            organizationId: number;
            userId: number;
            role: OrganizationRole;
            brands: any[];
        }): Promise<void> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const { organizationUsers: organizationUsersLocal } = await updateOrganizationUser_api({
                    organizationId,
                    userId,
                    role,
                    brands,
                });

                set({
                    organizationUsers: organizationUsersLocal,
                });
            } catch (error) {
                console.error('Failed to update organization user:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        updateUserBrand: async ({
            organizationId,
            brandId,
            users,
        }: {
            organizationId: number;
            brandId: number;
            users: {
                userId: number;
                permissionLevel: UserPermissionOnBrand;
            }[];
        }): Promise<void> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const { organizationUsers: organizationUsersLocal } = await updateUserBrand_api({
                    organizationId,
                    brandId,
                    users,
                });

                set({
                    organizationUsers: organizationUsersLocal,
                });
            } catch (error) {
                console.error('Failed to update user brand:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        deleteOrganizationBrand: async ({
            organizationId,
            brandId,
        }: {
            organizationId: number;
            brandId: number;
        }): Promise<void> => {
            const { organizationBrandIds } = get();
            const { isSymphonyAdmin } = useUser.getState();
            const { updateOrAddBrand, loadedBrandsDict, getBrands, removeBrand } = useBrand.getState();
            const myBrandWasRemoved = loadedBrandsDict?.[brandId]?.permissionLevel === UserPermissionOnBrand.OWNER;

            set({ isLoadingOrganizationAction: true });
            try {
                const res = await deleteOrganizationBrand_api({
                    organizationId,
                    brandId,
                });

                if (res) {
                    set({
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: organizationBrandIds?.filter(id => id !== brandId) || [],
                    });
                }
                const brand = deepCopy(loadedBrandsDict?.[brandId]);
                if (brand) {
                    if (myBrandWasRemoved) {
                        brand.tier = PlanType.FREE;
                        updateOrAddBrand(brand);
                    } else {
                        // if the brand removed is not my brand, remove it from local state
                        removeBrand(brandId);
                    }
                }

                await getBrands();
            } catch (error) {
                console.error('Failed to remove organization brand:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        deleteOrganizationUser: async ({
            organizationId,
            userId,
        }: {
            organizationId: number;
            userId: number;
        }): Promise<void> => {
            const { getOrganization } = get();
            const { updateOrAddBrands, getBrands, currentBrand, loadedBrands } = useBrand.getState();
            const { currentUser } = useUser.getState();
            const iLeftTheTeam = currentUser?.id === userId;
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await deleteOrganizationUser_api({
                    organizationId,
                    userId,
                });

                if (res) {
                    const {
                        organizationBrands,
                        removedBrands
                    } = res;

                    if (iLeftTheTeam) {
                        // remove the brands that i am not a part of
                        const brands = loadedBrands?.filter(brand => !organizationBrands.some(ob => ob.brand_id === brand.id)) || [];
                        updateOrAddBrands(brands);
                        set({
                            invitationStatus: undefined,
                            organization: undefined,
                            organizationUsers: [],
                            organizationBrandIds: [],
                        });
                    } else {
                        // remove the brands owned by the deleted user
                        const brands = loadedBrands?.filter(brand => !removedBrands.some(ob => ob.brand_id === brand.id)) || [];
                        updateOrAddBrands(brands);
                        await getOrganization({
                            organizationId,
                            brandId: currentBrand?.id,
                            userId: currentUser?.id,
                        });
                    }

                    await getBrands();
                }
            } catch (error) {
                console.error('Failed to remove organization user:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        deleteOrganization: async (): Promise<void> => {
            const { organization, resetOrganization } = get();
            const { getBrands } = useBrand.getState();
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await deleteOrganization_api({
                    organizationId: organization?.id!,
                });

                if (res) {
                    resetOrganization();
                    getBrands();
                }
            } catch (error) {
                console.error('Failed to delete organization:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },
    }))
);

export default useOrganizationV2;
